import { IdxStep } from '@mgmresorts/mgm-auth-js-sdk';
import { navigate } from 'hookrouter';
import { useCallback } from 'react';

import { captureException, captureMessage } from '../monitoring/logrocket';
import { useAuthContext } from '../utils/auth/AuthProvider';
import { IDENTITY_WIDGET_PATHS, IDW_FLOW_PARAM } from '../utils/constants';
import { getFeaturesByClientId } from '../utils/getFeaturesByClientId';
import { useUserAccount } from '../utils/userAccountContext';

import { useAppRedirect } from './useAppRedirect';
import { useMfaEnabled } from './useMfaEnabled';

export const useProcessSignInWithIdx = (flow: 'signup' | 'activate') => {
  const { authClient } = useAuthContext();
  const { mfaEnabled } = useMfaEnabled();
  const { mgmId, setUserMgmId } = useUserAccount();
  const redirectToApp = useAppRedirect();

  return useCallback(
    async (username: string, password: string): Promise<boolean> => {
      try {
        await authClient.idx.startAuthenticate(username);
        await authClient.idx.stepHandlers[IdxStep.Identify].Identify(username);

        const passwordHandler = authClient.idx.stepHandlers[IdxStep.AuthPassword];
        await passwordHandler.passwordAuth(username, password);

        if (authClient.idx.nextIdxStep === IdxStep.Success) {
          // Exchange the interaction code for tokens — this establishes the Okta session
          // cookie which is required for the subsequent /authorize redirect.
          authClient.idx.stepHandlers[IdxStep.Success].setTokens();

          // For third-party clients (e.g. Borgata, HGS) redirect to the client's
          // redirect_uri via Okta /authorize. setTokens() above establishes the Okta
          // session so /authorize can issue an authorization code without re-prompting
          // for credentials. We call the public SDK method handleOktaSignInWithRedirect to perform
          // a standard /authorize redirect.
          const { isAllowOktaSignInWithRedirect } = getFeaturesByClientId();

          if (isAllowOktaSignInWithRedirect) {
            const transaction = authClient.idx.idxTransaction ?? undefined;
            await authClient.handleOktaSignInWithRedirect(transaction);
            return true;
          }

          if (mfaEnabled) {
            if (!mgmId) {
              const accessToken = await authClient.getAccessToken();
              if (accessToken?.claims?.uid) {
                setUserMgmId(String(accessToken.claims.uid));
              }
            }
            navigate(IDENTITY_WIDGET_PATHS.MfaEnroll, true, { [IDW_FLOW_PARAM]: flow });
            return true;
          }

          redirectToApp();
          return true;
        }

        // Anything other than Success here is treated as a sign-in failure, falling back to legacy flow.
        return false;
      } catch (idxError) {
        captureMessage(
          `IDX sign-in after ${
            flow === 'signup' ? 'sign up' : 'account activation'
          } failed, falling back to legacy flow`,
          'sign-in',
          { flow },
        );
        captureException(idxError instanceof Error ? idxError : new Error(String(idxError)), 'sign-in', { flow });
        return false;
      }
    },
    [authClient, mfaEnabled, mgmId, redirectToApp, setUserMgmId, flow],
  );
};
